import React, { useCallback, useMemo, useRef, useState } from 'react';
import {
    AttributeChangeType,
    GenericSelectionAttribute,
    GenericVariableConfiguration,
    IProductAttributesProps,
    parseChangesToVariableConfiguration,
    ProductAttributesSelector,
} from '@rendering/components';

import { GenericSelector, ResourceType } from '@cimpress-technology/generic-selector';
import { AttributesToObject } from 'src/util/misc';
import {
    buildSizeChartFromProduct,
    type GenericSelectorProductType,
} from 'src/pages/editor/components/decorationSpecs/productPropertySizeChart';
import type { SizeChartResultType } from 'src/types/decorationSpecs';

interface IVariables {
    isReady: boolean | undefined;
    sku: string;
    skuVersion: number | undefined;
    variables: Record<string, string> | undefined;
    onChangeVariables: (sku: string, skuVersion: number, changes: AttributeChangeType) => void;
    token: string;
    /**
     * Reports the size chart resolved from this product's own attribute-model data, every time
     * the selection resolves (see `productPropertySizeChart.ts`). Optional so existing/other
     * callers of `VariablesV3` that don't care about the size chart aren't forced to pass a no-op.
     */
    onSizeChartChanged?: (sizeChart: SizeChartResultType | null) => void;
}

const options: IProductAttributesProps['options'] = {
    selector: {
        isColorSwatch: true,
        selectionResource: ResourceType.SURFACE,
        selectWithProductAttributes: false,
    },
    tooltip: {
        show: true,
    },
};

const setInitialValues = (attributes: GenericVariableConfiguration[]) => {
    const configuration: Record<string, GenericSelectionAttribute> = {};
    attributes?.forEach((item) => {
        if (item.key) {
            configuration[item.key] = {
                initialSelection: item.resolvedValue || '',
                isHidden: !item.isDisplayed,
            };
        }
    });
    return configuration;
};

const VariablesV3 = ({
    sku,
    skuVersion,
    variables,
    isReady,
    onChangeVariables,
    token,
    onSizeChartChanged,
}: IVariables) => {
    const [genericOptions, setGenericOptions] = useState(options);
    // `ProductAttributesSelector` forwards whatever ref it's given straight through to the
    // underlying `GenericSelector` instance (confirmed by reading @rendering/components' own
    // source — its internal `genericRef` is assigned to this exact ref in a `useEffect`), giving
    // direct, synchronous access to `.state.product` for `buildSizeChartFromProduct` below.
    const ref = useRef<GenericSelector | null>(null);

    const handleChanges = useCallback(
        (changes: AttributeChangeType) => {
            const newConfiguration = setInitialValues(parseChangesToVariableConfiguration(changes.attributes));
            setGenericOptions((current) =>
                Object.assign({}, current, {
                    selector: { ...current.selector, attributeConfigurations: newConfiguration },
                }),
            );
            const newVariables = changes.attributes?.filter((i) => i.isDisplayed === true);
            onChangeVariables &&
                JSON.stringify(variables) !== JSON.stringify(AttributesToObject(newVariables)) &&
                onChangeVariables(sku, skuVersion || 0, {
                    ...changes,
                    attributes: newVariables,
                });

            // Resolve the size chart from whatever GenericSelector has just settled on for this
            // change, using the freshly-resolved attributes (not the stale `variables` prop, which
            // reflects the *previous* render — see AttributesToObject's own use just above for the
            // same "changes.attributes is fresher" reasoning) merged over `variables` as a base for
            // any attribute this particular change didn't touch. Also fires for a product's very
            // first resolution, not just subsequent user-driven changes (confirmed live).
            if (onSizeChartChanged) {
                const product = ref.current?.state?.product as GenericSelectorProductType | undefined;
                const selections = { ...variables, ...AttributesToObject(changes.attributes) };
                onSizeChartChanged(buildSizeChartFromProduct(product, selections));
            }
        },
        [onChangeVariables, onSizeChartChanged, sku, skuVersion, variables],
    );
    const render = useMemo(() => {
        return isReady ? (
            <ProductAttributesSelector
                sku={sku}
                skuVersion={skuVersion}
                variables={variables}
                onChange={handleChanges}
                token={token}
                options={genericOptions}
                ref={ref}
            />
        ) : null;
    }, [sku, skuVersion, variables, handleChanges, isReady, genericOptions, token]);

    return (
        <div className='variables' id={token}>
            {render}
        </div>
    );
};

export default VariablesV3;
